import {
  Building2,
  Check,
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
  UserRound,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { switchChurchAndOpen } from '@/lib/switch-church-workspace';
import { groupChurchMemberships } from '@/pages/churches/group-church-memberships';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore, type ChurchMembershipSummary } from '@/stores/church-store';
import { useTheme, type ThemePreference } from '@/stores/theme-store';
import {
  campusInViewLabel,
  churchOrgName,
  resolveHeadOffice,
  shouldShowCampusInTopBar,
} from '@/utils/campus-display';
import { cn } from '@/utils/cn';

/**
 * Account menu in the top bar: profile, settings, appearance, church switch and sign-out.
 *
 * Church switching lives here (same placement as Weive CRM's institution menu),
 * not in the sidebar header — so the sidebar stays for product navigation.
 */
export const UserMenu = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();
  const { t: tChurches } = useTranslation('churches');
  const { t: tBranches } = useTranslation('branches');
  const { t: tAuth } = useTranslation('auth');
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const church = useChurchStore((state) => state.church);
  const headOffice = useChurchStore((state) => state.headOffice);
  const memberships = useChurchStore((state) => state.memberships);
  const selectChurch = useChurchStore((state) => state.selectChurch);
  const themePreference = useTheme((state) => state.preference);
  const setThemePreference = useTheme((state) => state.setPreference);

  const churchGroups = useMemo(() => groupChurchMemberships(memberships), [memberships]);
  if (!user) return null;

  const resolvedHeadOffice = resolveHeadOffice(church, headOffice, memberships);
  const showCampusInTopBar = church ? shouldShowCampusInTopBar(church, memberships) : false;

  const fullName = `${user.firstName} ${user.lastName}`;
  const canQuickSwitch = memberships.length > 1;

  const leave = async () => {
    await signOut();
    void navigate('/sign-in', { replace: true });
  };

  const openCampus = (row: ChurchMembershipSummary) => {
    if (row.church.id === church?.id) return;
    const group = churchGroups.find((item) =>
      item.campuses.some((campus) => campus.church.id === row.church.id),
    );
    const multiCampus = (group?.campuses.length ?? 0) > 1;
    const churchName = multiCampus
      ? `${group!.name} · ${campusInViewLabel(row.church, tBranches('switcher.headOffice'))}`
      : (group?.name ?? row.church.name);

    void switchChurchAndOpen({
      action: () => selectChurch(row.church.id),
      navigate,
      churchName,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="max-w-[14rem] gap-2"
          aria-label={t('userMenu.menu')}
        >
          <span className="hidden truncate text-body-sm md:inline">{fullName}</span>
          <Avatar src={user.avatarUrl} name={fullName} size="sm" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <p className="truncate text-body font-medium text-fg">{fullName}</p>
          <p className="truncate text-caption font-normal text-fg-secondary">{user.email}</p>
        </DropdownMenuLabel>

        {church ? (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <p className="text-overline font-semibold text-fg-tertiary uppercase">
                {tChurches('switcher.current')}
              </p>
              <p className="mt-0.5 truncate text-body-sm font-medium text-fg">
                {churchOrgName(church, resolvedHeadOffice)}
              </p>
              {showCampusInTopBar ? (
                <p className="truncate text-caption text-fg-secondary">
                  {campusInViewLabel(church, tBranches('switcher.headOffice'))}
                </p>
              ) : null}
            </div>
          </>
        ) : null}

        {canQuickSwitch ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ChevronsUpDown className="size-4" aria-hidden />
              {tChurches('switcher.switch')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64">
              {churchGroups.map((group, index) => {
                const multiCampus = group.campuses.length > 1;

                return (
                  <div key={group.orgId}>
                    {index > 0 ? <DropdownMenuSeparator /> : null}
                    {multiCampus || churchGroups.length > 1 ? (
                      <DropdownMenuLabel className="normal-case tracking-normal text-fg">
                        {group.name}
                      </DropdownMenuLabel>
                    ) : null}
                    {group.campuses.map((row) => {
                      const selected = row.church.id === church?.id;
                      const label = multiCampus
                        ? campusInViewLabel(row.church, tBranches('switcher.headOffice'))
                        : group.name;

                      return (
                        <DropdownMenuItem
                          key={row.church.id}
                          disabled={selected}
                          onSelect={() => openCampus(row)}
                        >
                          <Check
                            className={cn(
                              'size-4 shrink-0',
                              selected ? 'opacity-100' : 'opacity-0',
                            )}
                            aria-hidden
                          />
                          {!multiCampus && churchGroups.length === 1 ? (
                            <Building2 className="size-4 shrink-0" aria-hidden />
                          ) : null}
                          <span className="min-w-0 flex-1 truncate">{label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => void navigate('/churches/select')}>
                {t('userMenu.allChurches')}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuItem onSelect={() => void navigate('/churches/select')}>
            <ChevronsUpDown className="size-4" aria-hidden />
            {tChurches('switcher.switch')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => void navigate('/account')}>
          <UserRound className="size-4" aria-hidden />
          {t('userMenu.profile')}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={() => void navigate('/settings')}>
          <Settings className="size-4" aria-hidden />
          {tCommon('nav.settings')}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon preference={themePreference} />
            {tCommon('theme.label')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-48">
            {(
              [
                { value: 'light', label: tCommon('theme.light'), icon: Sun },
                { value: 'dark', label: tCommon('theme.dark'), icon: Moon },
                { value: 'system', label: tCommon('theme.system'), icon: Monitor },
              ] as const
            ).map(({ value, label, icon: Icon }) => {
              const selected = themePreference === value;
              return (
                <DropdownMenuItem
                  key={value}
                  onSelect={(event) => {
                    // Keep the menu open so people can try options without reopening.
                    event.preventDefault();
                    setThemePreference(value);
                  }}
                >
                  <Check
                    className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                    aria-hidden
                  />
                  <Icon className="size-4 shrink-0" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem destructive onSelect={() => void leave()}>
          <LogOut className="size-4" aria-hidden />
          {tAuth('signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ThemeIcon = ({ preference }: { preference: ThemePreference }) => {
  if (preference === 'dark') return <Moon className="size-4" aria-hidden />;
  if (preference === 'light') return <Sun className="size-4" aria-hidden />;
  return <Monitor className="size-4" aria-hidden />;
};
